import { useCallback, useEffect, useRef, useState } from "react";
import useAxios from "../../../utils/useAxios";
import { URL as UrlUtils } from "../../../libraries/http/url";

interface ISearchCollection {
  search?: any;
  id?: string | number;
  limit?: string | number;
}

export const useGetCollectionsHook = ({
  search,
  id,
  limit,
}: ISearchCollection) => {
  const axios = useAxios();
  const [data, setData] = useState<any | any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const getCollection = useCallback(
    async ({ search, id, limit }: ISearchCollection) => {
      // Abort previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const searchParams = UrlUtils.createSearchParams({
        ...search,
        id,
        limit: limit ?? 50,
      });

      const url = !!id
        ? `/artwork/collection/${id}`
        : `/artwork/collection/?${searchParams.toString()}`;

      try {
        setIsLoading(true);
        const response = await axios.get(url, { signal: controller.signal });

        if (response.status === 200) {
          setData(response.data);
        }

        return response.data
      } catch (e) {
        if ((e as any).name === "CanceledError") return; // ignore abort noise
      } finally {
        if (abortControllerRef.current === controller) setIsLoading(false);
      }
    },
    [axios],
  );

  useEffect(() => {
    getCollection({ search, id, limit });

    return () => {
      // Cleanup on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [search, id, limit]);

  return { data, isLoading, getCollection };
};
